import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { clearTokens, getRefreshToken } from '../../lib/tokens';
import { Avatar } from '../ui/Avatar';
import { Modal } from '../ui/Modal';

type AppHeaderProps = {
  title: string;
  breadcrumb: string;
  userInitials: string;
  userAvatarUrl?: string | null;
  right?: ReactNode;
};

export function AppHeader({ title, breadcrumb, userInitials, userAvatarUrl, right }: AppHeaderProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await api.logout(refreshToken).catch(() => {});
    }
    clearTokens();
    navigate('/login');
  };

  return (
    <header className="flex h-[72px] shrink-0 items-center justify-between border-b border-border-default bg-bg-surface px-8">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">{title}</h1>
        <p className="mt-1 text-[10px] text-text-secondary">{breadcrumb}</p>
      </div>
      <div className="flex items-center gap-4">
        {right}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            title="Профиль"
            className="flex overflow-hidden rounded-full"
          >
            <Avatar initials={userInitials} imageUrl={userAvatarUrl} size={38} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-44 rounded-xl border border-border-default bg-bg-surface p-1.5 shadow-lg">
              <Link
                to="/app/profile"
                onClick={() => setMenuOpen(false)}
                className="flex h-10 items-center rounded-lg px-3 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
              >
                Параметры
              </Link>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmOpen(true);
                }}
                className="flex h-10 w-full items-center rounded-lg px-3 text-left text-[13px] font-medium text-status-danger hover:bg-bg-surface-secondary"
              >
                Выход
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <h2 className="text-lg font-semibold text-text-primary">Выйти из аккаунта?</h2>
        <p className="mt-2 text-sm text-text-secondary">Точно хотите выйти? Для продолжения работы нужно будет войти заново.</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            className="flex h-10 items-center rounded-md border border-border-strong bg-bg-surface px-4 text-sm font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Нет
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex h-10 items-center rounded-md bg-status-danger px-4 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Да, выйти
          </button>
        </div>
      </Modal>
    </header>
  );
}
